import { useEffect, useState } from "react";
import { Menu, Search, Bell as BellIcon, ChevronDown, LogOut, User, Settings, Route } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import UserAvatar from "@/components/common/UserAvatar";
import SearchBar from "@/components/common/SearchBar";
import { Button } from "@/components/ui/button";
import { HelpButton } from "@/components/help";
import { useTour } from "@/components/tour";
import NotificationBell from "@/components/notifications/NotificationBell";
import NotificationSettings from "@/components/notifications/NotificationSettings";
import ThemeToggle from "@/components/layout/ThemeToggle";
import MyAccountDialog from "@/components/layout/MyAccountDialog";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

interface HeaderProps {
  toggleSidebar: () => void;
}

export default function Header({ toggleSidebar }: HeaderProps) {
  const { user, logout } = useAuth();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [, setLocation] = useLocation();
  const { startTour } = useTour();
  const { toast } = useToast();

  useEffect(() => {
    if (!user) {
      return;
    }

    const storageKey = "notification-preferences-onboarding-v2";
    if (window.localStorage.getItem(storageKey)) {
      return;
    }

    window.localStorage.setItem(storageKey, "seen");
    toast({
      title: "Control Email Notifications",
      description: "You can manage notification email, push, and in-app preferences from Settings.",
      action: (
        <ToastAction altText="Open notification settings" onClick={() => setLocation("/settings?tab=notifications")}>
          Open
        </ToastAction>
      ),
    });
  }, [setLocation, toast, user]);

  const handleStartTour = () => {
    // Navigate to dashboard first, then start tour
    setLocation("/");
    setTimeout(() => startTour('dashboard'), 500);
  };

  return (
    <header className="bg-background border-b border-border py-2 px-4 flex items-center justify-between shadow-sm z-10">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="hidden md:flex lg:hidden text-muted-foreground hover:text-primary"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <a href="/" className="flex items-center gap-2">
          <img src="/logo/dynamatixLogo.png" alt="Dynamatix" className="h-8" />
        </a>
      </div>

      <div className="hidden md:flex flex-grow max-w-md mx-4" data-tour="search-bar">
        <SearchBar />
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setMobileSearchOpen(true)}
        >
          <Search className="h-5 w-5" />
        </Button>

        <span data-tour="help-button">
          <HelpButton tooltip="Get help (press ?)" />
        </span>

        <NotificationBell />

        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 p-1">
              <UserAvatar name={user?.name || "User"} src={user?.photoUrl || undefined} />
              <span className="text-sm font-medium hidden md:inline">{user?.name || "User"}</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => setLocation("/my-profile")}>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <MyAccountDialog
                defaultTab="settings"
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                }
              />
              <NotificationSettings
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <BellIcon className="mr-2 h-4 w-4" />
                    <span>Notification Settings</span>
                  </DropdownMenuItem>
                }
              />
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleStartTour}>
              <Route className="mr-2 h-4 w-4" />
              <span>Start Tour</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {mobileSearchOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 flex items-start pt-20 px-4" onClick={() => setMobileSearchOpen(false)}>
          <div className="bg-background p-4 rounded-lg shadow-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center mb-4">
              <div className="flex-grow">
                <SearchBar />
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="ml-2"
                onClick={() => setMobileSearchOpen(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </Button>
            </div>
            <div className="py-2">
              <p className="text-xs text-muted-foreground mb-2">Recent Searches</p>
              <div className="space-y-2">
                <a href="/knowledge" className="block px-3 py-2 hover:bg-accent rounded text-sm">Vacation policy</a>
                <a href="/knowledge" className="block px-3 py-2 hover:bg-accent rounded text-sm">Engineering wiki</a>
                <a href="/knowledge" className="block px-3 py-2 hover:bg-accent rounded text-sm">Project templates</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
